/**
 * textDiff — a pure, dependency-free line diff for the CRM version history.
 *
 * Every versioned thing in the CRM (study overview, scripts, prescreens, forms,
 * review criteria, prompts, flows) is flattened server-side into one canonical
 * human-readable text per version; this module diffs two of those texts.
 *
 * - `diffLines` is an exact LCS line diff. The common prefix and suffix are
 *   trimmed first so the DP only covers the changed middle; above
 *   MAX_LINE_CELLS DP cells the middle falls back to "delete every old line,
 *   add every new line" (content is never lost, only alignment).
 * - Inside each changed run the removed lines are listed before the added ones
 *   (the unified-diff convention) and paired positionally. A pair that is
 *   similar enough gets word-level `segments` (a token LCS over words,
 *   whitespace and punctuation), so the view can emphasise only the words that
 *   changed instead of the whole line.
 * - `collapseUnchanged` folds long unchanged runs into `gap` markers that keep
 *   `context` lines either side of every change; `diffStats` counts lines.
 *
 * No imports on purpose: CI runs this file's suite in the pure-jest lane.
 * Borrowed from the AgentTuning `lineDiff` and backend `crmSourceDiff.js`.
 */

export type DiffLineType = 'same' | 'add' | 'del';

export interface DiffSegment {
  text: string;
  /** True for the words that differ from the paired line. */
  changed: boolean;
}

export interface DiffRow {
  type: DiffLineType;
  text: string;
  /** 1-based line number in the old text ('same' and 'del' rows). */
  oldNo?: number;
  /** 1-based line number in the new text ('same' and 'add' rows). */
  newNo?: number;
  /** Word-level emphasis, present only on a del/add row paired with a similar line. */
  segments?: DiffSegment[];
}

/** A run of unchanged lines folded away by `collapseUnchanged`. */
export interface DiffGap {
  type: 'gap';
  /** How many unchanged rows are hidden. */
  count: number;
  /** Index into the rows passed to `collapseUnchanged` of the first hidden row. */
  start: number;
}

export type DisplayRow = DiffRow | DiffGap;

export interface DiffStats {
  added: number;
  removed: number;
}

/** Above this many line-DP cells the changed middle falls back to del-all/add-all. */
export const MAX_LINE_CELLS = 4_000_000;
/** Token-DP cell cap for a single line pair (very long paragraphs skip word emphasis). */
export const MAX_TOKEN_CELLS_PER_PAIR = 250_000;
/** Token-DP cell budget for one whole diff. */
export const MAX_TOKEN_CELLS_TOTAL = 4_000_000;
/** Minimum similarity (shared non-whitespace characters) for word-level emphasis. */
export const WORD_SIMILARITY_THRESHOLD = 0.5;
/** A fold smaller than this is not worth a "Show N unchanged lines" row. */
const MIN_GAP = 2;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Split a text into lines: CRLF / lone CR normalised to LF, and a single
 * trailing newline treated as a terminator rather than an extra empty line.
 * An empty (or null) text has zero lines.
 */
export function splitLines(text: string | null | undefined): string[] {
  const s = String(text ?? '').replace(/\r\n?/g, '\n');
  if (s === '') return [];
  const lines = s.split('\n');
  if (lines[lines.length - 1] === '') lines.pop();
  return lines;
}

/** 0 = same, 1 = delete (old only), 2 = add (new only). */
type Op = 0 | 1 | 2;

/** Exact LCS edit script over two string arrays (flat typed-array DP). */
function lcsScript(a: string[], b: string[]): Op[] {
  const n = a.length;
  const m = b.length;
  if (!n) return new Array<Op>(m).fill(2);
  if (!m) return new Array<Op>(n).fill(1);
  const w = m + 1;
  const size = (n + 1) * w;
  // The LCS length never exceeds min(n, m); Uint16 covers every capped input.
  const dp = Math.min(n, m) < 0xffff ? new Uint16Array(size) : new Uint32Array(size);
  for (let i = n - 1; i >= 0; i -= 1) {
    const row = i * w;
    const next = row + w;
    for (let j = m - 1; j >= 0; j -= 1) {
      dp[row + j] = a[i] === b[j]
        ? dp[next + j + 1] + 1
        : Math.max(dp[next + j], dp[row + j + 1]);
    }
  }
  const ops: Op[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) { ops.push(0); i += 1; j += 1; }
    else if (dp[(i + 1) * w + j] >= dp[i * w + j + 1]) { ops.push(1); i += 1; }
    else { ops.push(2); j += 1; }
  }
  while (i < n) { ops.push(1); i += 1; }
  while (j < m) { ops.push(2); j += 1; }
  return ops;
}

// Words (Latin + Latin-1/Extended letters, digits, underscore), whitespace
// runs, and any other single character (punctuation, dashes, curly quotes).
const TOKEN_RE = /\s+|[A-Za-z0-9_À-ɏ]+|[\s\S]/g;
const WS_RE = /^\s+$/;

function tokenize(line: string): string[] {
  return line.match(TOKEN_RE) || [];
}

function nonWsLength(tokens: string[]): number {
  let len = 0;
  for (const t of tokens) if (!WS_RE.test(t)) len += t.length;
  return len;
}

/**
 * Merge per-token flags into segments. A space between two changed words joins
 * them into one phrase, and a changed phrase never starts or ends on
 * whitespace (the LCS may pick either of two equal spaces; the emphasis should
 * sit on the words). A change that is ONLY whitespace keeps its emphasis.
 */
function toSegments(tokens: string[], changed: boolean[]): DiffSegment[] {
  const flags = changed.slice();
  for (let k = 1; k < tokens.length - 1; k += 1) {
    if (!flags[k] && WS_RE.test(tokens[k]) && flags[k - 1] && flags[k + 1]) flags[k] = true;
  }
  for (let k = 0; k < tokens.length;) {
    if (!flags[k]) { k += 1; continue; }
    let end = k;
    while (end < tokens.length && flags[end]) end += 1;
    let hasWord = false;
    for (let t = k; t < end; t += 1) if (!WS_RE.test(tokens[t])) { hasWord = true; break; }
    if (hasWord) {
      for (let t = k; t < end && WS_RE.test(tokens[t]); t += 1) flags[t] = false;
      for (let t = end - 1; t >= k && WS_RE.test(tokens[t]); t -= 1) flags[t] = false;
    }
    k = end;
  }
  const out: DiffSegment[] = [];
  for (let k = 0; k < tokens.length; k += 1) {
    const last = out[out.length - 1];
    if (last && last.changed === flags[k]) last.text += tokens[k];
    else out.push({ text: tokens[k], changed: flags[k] });
  }
  return out;
}

interface TokenBudget { cells: number }

/**
 * Word-level segments for a removed/added line pair, or null when the two
 * lines are too different (the whole line is then the change) or too long.
 */
function wordSegments(
  oldLine: string,
  newLine: string,
  budget: TokenBudget,
): { old: DiffSegment[]; new: DiffSegment[] } | null {
  const ta = tokenize(oldLine);
  const tb = tokenize(newLine);
  if (!ta.length || !tb.length) return null;
  const lenA = nonWsLength(ta);
  const lenB = nonWsLength(tb);
  if (!lenA || !lenB) return null;
  // Upper bound on the similarity: even a perfect overlap can't beat this.
  if ((2 * Math.min(lenA, lenB)) / (lenA + lenB) < WORD_SIMILARITY_THRESHOLD) return null;
  const cells = ta.length * tb.length;
  if (cells > MAX_TOKEN_CELLS_PER_PAIR || budget.cells + cells > MAX_TOKEN_CELLS_TOTAL) return null;
  budget.cells += cells;

  const ops = lcsScript(ta, tb);
  const changedA = new Array<boolean>(ta.length).fill(true);
  const changedB = new Array<boolean>(tb.length).fill(true);
  let common = 0;
  let i = 0;
  let j = 0;
  for (const op of ops) {
    if (op === 0) {
      changedA[i] = false;
      changedB[j] = false;
      if (!WS_RE.test(ta[i])) common += ta[i].length;
      i += 1;
      j += 1;
    } else if (op === 1) {
      i += 1;
    } else {
      j += 1;
    }
  }
  if ((2 * common) / (lenA + lenB) < WORD_SIMILARITY_THRESHOLD) return null;
  return { old: toSegments(ta, changedA), new: toSegments(tb, changedB) };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Line diff of two texts. Rows come back in display order: unchanged lines as
 * 'same', and each changed run as its 'del' rows followed by its 'add' rows.
 */
export function diffLines(oldText: string | null | undefined, newText: string | null | undefined): DiffRow[] {
  const a = splitLines(oldText);
  const b = splitLines(newText);

  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) start += 1;
  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) { endA -= 1; endB -= 1; }

  const midA = a.slice(start, endA);
  const midB = b.slice(start, endB);
  const fallback = midA.length * midB.length > MAX_LINE_CELLS;
  const ops: Op[] = fallback
    ? [...new Array<Op>(midA.length).fill(1), ...new Array<Op>(midB.length).fill(2)]
    : lcsScript(midA, midB);

  const rows: DiffRow[] = [];
  let oldNo = 0;
  let newNo = 0;
  for (let k = 0; k < start; k += 1) {
    oldNo += 1; newNo += 1;
    rows.push({ type: 'same', text: a[k], oldNo, newNo });
  }

  const budget: TokenBudget = { cells: 0 };
  let ai = 0;
  let bi = 0;
  let k = 0;
  while (k < ops.length) {
    if (ops[k] === 0) {
      oldNo += 1; newNo += 1;
      rows.push({ type: 'same', text: midA[ai], oldNo, newNo });
      ai += 1; bi += 1; k += 1;
      continue;
    }
    const dels: DiffRow[] = [];
    const adds: DiffRow[] = [];
    while (k < ops.length && ops[k] !== 0) {
      if (ops[k] === 1) {
        oldNo += 1;
        dels.push({ type: 'del', text: midA[ai], oldNo });
        ai += 1;
      } else {
        newNo += 1;
        adds.push({ type: 'add', text: midB[bi], newNo });
        bi += 1;
      }
      k += 1;
    }
    // Positional pairing is meaningless for the unaligned fallback hunk.
    if (!fallback) {
      const pairs = Math.min(dels.length, adds.length);
      for (let p = 0; p < pairs; p += 1) {
        const seg = wordSegments(dels[p].text, adds[p].text, budget);
        if (seg) {
          dels[p].segments = seg.old;
          adds[p].segments = seg.new;
        }
      }
    }
    rows.push(...dels, ...adds);
  }

  for (let t = endA; t < a.length; t += 1) {
    oldNo += 1; newNo += 1;
    rows.push({ type: 'same', text: a[t], oldNo, newNo });
  }
  return rows;
}

/**
 * Fold unchanged runs, keeping `context` lines next to each change. A leading
 * run keeps only its tail, a trailing run only its head, and a diff with no
 * changes at all folds into a single gap. Runs that would hide fewer than two
 * lines are left expanded.
 */
export function collapseUnchanged(rows: DiffRow[], context = 3): DisplayRow[] {
  const ctx = Math.max(0, Math.floor(Number.isFinite(context) ? context : 3));
  const out: DisplayRow[] = [];
  const n = rows.length;
  let i = 0;
  while (i < n) {
    if (rows[i].type !== 'same') { out.push(rows[i]); i += 1; continue; }
    let j = i;
    while (j < n && rows[j].type === 'same') j += 1;
    const keepHead = i === 0 ? 0 : ctx;
    const keepTail = j === n ? 0 : ctx;
    const hidden = j - i - keepHead - keepTail;
    if (hidden >= MIN_GAP) {
      for (let t = i; t < i + keepHead; t += 1) out.push(rows[t]);
      out.push({ type: 'gap', count: hidden, start: i + keepHead });
      for (let t = j - keepTail; t < j; t += 1) out.push(rows[t]);
    } else {
      for (let t = i; t < j; t += 1) out.push(rows[t]);
    }
    i = j;
  }
  return out;
}

/** Added / removed line counts (gap markers are ignored). */
export function diffStats(rows: ReadonlyArray<DisplayRow>): DiffStats {
  let added = 0;
  let removed = 0;
  for (const r of rows) {
    if (r.type === 'add') added += 1;
    else if (r.type === 'del') removed += 1;
  }
  return { added, removed };
}
